/* eslint-disable no-unused-vars */
/* eslint-disable no-shadow */

import { SelectedAgent } from '@/components/agents-table/AgentActionMenu.types';
import { Agent_SortOrder, Agent_Status } from '@/graphql/codegen/generated/graphql';
import { OsType } from '@/modules/platformAuthentication/platformAuthentication.const';
import { ComputedRef, Ref } from 'vue';
import { AgentsPagePersistentDataType } from '@/views/agents/agents.helpers';
import {
  AgentsTableLinuxColumns,
  DefaultTableLinuxColumns,
  MapAgentSortFieldToColumnsLinux,
  MapColumnsToAgentSortFieldLinux,
  NonSortableLinuxColumns,
  SortableLinuxColumns,
} from './OS/agents-table.linux';
import {
  AgentsTableWindowsColumns,
  DefaultTableWindowsColumns,
  MapAgentSortFieldToColumnsWindows,
  MapColumnsToAgentSortFieldWindows,
  NonSortableWindowsColumns,
  SortableWindowsColumns,
} from './OS/agents-table.windows';

export enum AgentsFilterSelection {
  All = 'All',
  Online = 'Online',
  Offline = 'Offline',
  Cloud = 'Cloud',
  IncompatibleVersion = 'IncompatibleVersion',
  DuplicateLicense = 'DuplicateLicense',
}

export type AgentsSelected = {
  selectedAgentIds: Readonly<Ref<Readonly<Set<string>>>>;
  loading: Readonly<Ref<boolean>>;
  selectByIds: (newIds: Set<string>, newSelectedAgents?: SelectedAgent[] | null) => void;
  clearSelection: () => void;
  selectByFilter: (filterType: AgentsFilterSelection) => void;
  removeAgentFromSelectedAgentIds: (agentId: string) => void;
};

export enum AgentStatusMeta {
  Incompatible = 'Incompatible',
  Duplicate = 'Duplicate',
}

export type AgentStatus = Agent_Status | AgentStatusMeta;

export type TableColumn = {
  name: AgentsTableWindowsColumns & AgentsTableLinuxColumns;
  width: number;
  minWidth?: number;
  sortable: true;
  locked?: boolean;
};

export type TableColumns<T extends OsType> = Array<{
  name: T extends OsType.Windows ? AgentsTableWindowsColumns : T extends OsType.Linux ? AgentsTableLinuxColumns : never;
  width: number;
  minWidth?: number;
  sortable?: true;
  locked?: boolean;
}>;

export type TableEditorGetter<T extends OsType> = T extends OsType.Windows
  ? ComputedRef<AgentsPagePersistentDataType['tableEditor']['windows']>
  : T extends OsType.Linux
  ? ComputedRef<AgentsPagePersistentDataType['tableEditor']['linux']>
  : never;

export type TableEditorSetter<T extends OsType> = T extends OsType.Windows
  ? (value: AgentsPagePersistentDataType['tableEditor']['windows']) => void
  : T extends OsType.Linux
  ? (value: AgentsPagePersistentDataType['tableEditor']['linux']) => void
  : never;

export type TableEditor = {
  get: {
    [OsType.Windows]: TableEditorGetter<OsType.Windows>;
    [OsType.Linux]: TableEditorGetter<OsType.Linux>;
    [OsType.Mac]: TableEditorGetter<OsType.Linux>;
  };
  set: {
    [OsType.Windows]: TableEditorSetter<OsType.Windows>;
    [OsType.Linux]: TableEditorSetter<OsType.Linux>;
    [OsType.Mac]: TableEditorSetter<OsType.Linux>;
  };
};

export type TableEditorUnified =
  | {
      get: Record<
        OsType,
        ComputedRef<{
          columnsOrder: AgentsPagePersistentDataType['tableEditor']['windows']['columnsOrder'] & AgentsPagePersistentDataType['tableEditor']['linux']['columnsOrder'];
          columnsFilter: Partial<AgentsPagePersistentDataType['tableEditor']['windows']['columnsFilter'] & AgentsPagePersistentDataType['tableEditor']['linux']['columnsFilter']>;
        }>
      >;

      set: Record<OsType, (value: AgentsPagePersistentDataType['tableEditor']['linux'] | AgentsPagePersistentDataType['tableEditor']['windows']) => void>;
    }
  | undefined
  | null;

export type ColumnsFilter = Partial<Record<AgentsTableLinuxColumns, boolean>> & Partial<Record<AgentsTableWindowsColumns, boolean>>;

export type MapColumnsToAgentSortField<T extends OsType> = {
  name: T extends OsType.Windows ? keyof MapColumnsToAgentSortFieldWindows : T extends OsType.Linux ? keyof MapColumnsToAgentSortFieldLinux : never;
  width: number;
  minWidth?: number;
  sortable?: true;
  locked?: boolean;
  defaultColumn?: boolean;
  defaultSortOrder?: Agent_SortOrder;
};

export type SortableColumns = {
  [OsType.Windows]: SortableWindowsColumns;
  [OsType.Linux]: SortableLinuxColumns;
  [OsType.Mac]: SortableLinuxColumns;
};

export type NonSortableColumns = {
  [OsType.Windows]: NonSortableWindowsColumns;
  [OsType.Linux]: NonSortableLinuxColumns;
  [OsType.Mac]: NonSortableLinuxColumns;
};

export type MapAgentSortFieldToColumns = {
  [OsType.Windows]: MapAgentSortFieldToColumnsWindows;
  [OsType.Linux]: MapAgentSortFieldToColumnsLinux;
  [OsType.Mac]: MapAgentSortFieldToColumnsLinux;
};

export type DefaultTableColumns = {
  [OsType.Windows]: DefaultTableWindowsColumns;
  [OsType.Linux]: DefaultTableLinuxColumns;
  [OsType.Mac]: DefaultTableLinuxColumns;
};
